import { Button, Modal } from '@hive/component';
import { type TagLike, TagsInlineEditor } from '@hive/core/components/tags';
import {
  IntegrationService,
  IntegrationTypeIcon,
} from '@hive/core/modules/integration';
import type { ReadwiseConfig } from '@hive/core/modules/integration/type';
import { TagService } from '@hive/core/modules/tag';
import { useI18n } from '@hive/i18n';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { IntegrationCardIcon } from '../card';
import {
  IntegrationSettingItem,
  IntegrationSettingTextRadioGroup,
  type IntegrationSettingTextRadioGroupItem,
  IntegrationSettingToggle,
} from '../setting';
import * as styles from './setting-dialog.css';
import { readwiseTrack } from './track';

export const SettingDialog = ({
  onClose,
  onImport,
}: {
  onClose: () => void;
  onImport: () => void;
}) => {
  const t = useI18n();
  return (
    <Modal
      open
      onOpenChange={onClose}
      contentOptions={{ className: styles.dialog }}
    >
      <header className={styles.header}>
        <IntegrationCardIcon className={styles.headerIcon}>
          <IntegrationTypeIcon type="readwise" />
        </IntegrationCardIcon>
        <div>
          <h1 className={styles.headerTitle}>
            {t['com.hive.integration.readwise.name']()}
          </h1>
          <p className={styles.headerCaption}>
            {t['com.hive.integration.readwise.setting.caption']()}
          </p>
        </div>
      </header>
      <ul className={styles.settings}>
        <TagsSetting />
        <Divider />
        <NewHighlightSetting />
        <Divider />
        <UpdateStrategySetting />
        <Divider />
        <StartImport onImport={onImport} />
      </ul>
    </Modal>
  );
};

const trackModifySetting = (
  item: 'New' | 'Update' | 'Tag',
  option: 'on' | 'off',
  method?: 'append' | 'override'
) => {
  readwiseTrack.modifyIntegrationSettings({
    item,
    option,
    method,
  });
};

const Divider = () => {
  return <li className={styles.divider} />;
};

const NewHighlightSetting = () => {
  const t = useI18n();
  const readwise = useService(IntegrationService).readwise;
  const syncNewHighlights = useLiveData(
    useMemo(() => readwise.setting$('syncNewHighlights'), [readwise])
  );

  const toggle = useCallback(
    (value: boolean) => {
      trackModifySetting('New', value ? 'on' : 'off');
      readwise.updateSetting('syncNewHighlights', value);
    },
    [readwise]
  );

  return (
    <li>
      <IntegrationSettingToggle
        checked={!!syncNewHighlights}
        name={t['com.hive.integration.readwise.setting.sync-new-name']()}
        desc={t['com.hive.integration.readwise.setting.sync-new-desc']()}
        onChange={toggle}
      />
    </li>
  );
};

const UpdateStrategySetting = () => {
  const t = useI18n();
  const readwise = useService(IntegrationService).readwise;
  const updateStrategy = useLiveData(
    useMemo(() => readwise.setting$('updateStrategy'), [readwise])
  );

  const toggle = useCallback(
    (value: boolean) => {
      trackModifySetting('Update', value ? 'on' : 'off', 'append');
      if (!value) readwise.updateSetting('updateStrategy', undefined);
      else readwise.updateSetting('updateStrategy', 'append');
    },
    [readwise]
  );

  const handleUpdate = useCallback(
    (value: ReadwiseConfig['updateStrategy']) => {
      trackModifySetting('Update', 'on', value);
      readwise.updateSetting('updateStrategy', value);
    },
    [readwise]
  );

  const strategies = useMemo(
    () =>
      [
        {
          name: t[
            'com.hive.integration.readwise.setting.update-append-name'
          ](),
          desc: t[
            'com.hive.integration.readwise.setting.update-append-desc'
          ](),
          value: 'append',
        },
        {
          name: t[
            'com.hive.integration.readwise.setting.update-override-name'
          ](),
          desc: t[
            'com.hive.integration.readwise.setting.update-override-desc'
          ](),
          value: 'override',
        },
      ] satisfies IntegrationSettingTextRadioGroupItem[],
    [t]
  );

  return (
    <>
      <li>
        <IntegrationSettingToggle
          checked={!!updateStrategy}
          name={t['com.hive.integration.readwise.setting.update-name']()}
          desc={t['com.hive.integration.readwise.setting.update-desc']()}
          onChange={toggle}
        />
      </li>
      <li
        className={styles.updateStrategyGroup}
        data-collapsed={!updateStrategy}
      >
        <div className={styles.updateStrategyGroupContent}>
          <h6 className={styles.updateStrategyLabel}>
            {t['com.hive.integration.readwise.setting.update-strategy']()}
          </h6>
          <IntegrationSettingTextRadioGroup
            items={strategies}
            checked={updateStrategy}
            onChange={handleUpdate}
          />
        </div>
      </li>
    </>
  );
};

const StartImport = ({ onImport }: { onImport: () => void }) => {
  const t = useI18n();
  const readwise = useService(IntegrationService).readwise;

  const handleImport = useCallback(() => {
    const lastImportedAt = readwise.setting$('lastImportedAt').value;
    readwiseTrack.startIntegrationImport({
      method: lastImportedAt ? 'withtimestamp' : 'new',
      control: 'Readwise settings',
    });
    onImport();
  }, [onImport, readwise]);

  return (
    <IntegrationSettingItem
      name={t['com.hive.integration.readwise.setting.start-import-name']()}
      desc={t['com.hive.integration.readwise.setting.start-import-desc']()}
    >
      <Button onClick={handleImport}>
        {t['com.hive.integration.readwise.setting.start-import-button']()}
      </Button>
    </IntegrationSettingItem>
  );
};

const TagsSetting = () => {
  const t = useI18n();
  const tagService = useService(TagService);
  const readwise = useService(IntegrationService).readwise;
  const allTags = useLiveData(tagService.tagList.tags$);
  const tagColors = tagService.tagColors;
  const tagIds = useLiveData(
    useMemo(() => readwise.setting$('tags'), [readwise])
  );
  const adaptedTags = useLiveData(
    useMemo(() => {
      return LiveData.computed(get => {
        return allTags.map(tag => ({
          id: tag.id,
          value: get(tag.value$),
          color: get(tag.color$),
        }));
      });
    }, [allTags])
  );
  const adaptedTagColors = useMemo(() => {
    return tagColors.map(color => ({
      id: color[0],
      value: color[1],
      name: color[0],
    }));
  }, [tagColors]);

  const updateReadwiseTags = useCallback(
    (tagIds: string[]) => {
      readwise.updateSetting(
        'tags',
        tagIds.filter(id => !!allTags.some(tag => tag.id === id))
      );
    },
    [allTags, readwise]
  );

  const onCreateTag = useCallback(
    (name: string, color: string) => {
      const tag = tagService.tagList.createTag(name, color);
      return { id: tag.id, value: tag.value$.value, color: tag.color$.value };
    },
    [tagService.tagList]
  );
  const onSelectTag = useCallback(
    (tagId: string) => {
      trackModifySetting('Tag', 'on');
      updateReadwiseTags([...(tagIds ?? []), tagId]);
    },
    [tagIds, updateReadwiseTags]
  );
  const onDeselectTag = useCallback(
    (tagId: string) => {
      trackModifySetting('Tag', 'off');
      updateReadwiseTags(tagIds?.filter(id => id !== tagId) ?? []);
    },
    [tagIds, updateReadwiseTags]
  );
  const onDeleteTag = useCallback(
    (tagId: string) => {
      if (tagIds?.includes(tagId)) {
        trackModifySetting('Tag', 'off');
      }
      tagService.tagList.deleteTag(tagId);
      updateReadwiseTags(tagIds ?? []);
    },
    [tagIds, updateReadwiseTags, tagService.tagList]
  );
  const onTagChange = useCallback(
    (id: string, property: keyof TagLike, value: string) => {
      if (property === 'value') {
        tagService.tagList.tagByTagId$(id).value?.rename(value);
      } else if (property === 'color') {
        tagService.tagList.tagByTagId$(id).value?.changeColor(value);
      }
    },
    [tagService.tagList]
  );
  return (
    <li>
      <h6 className={styles.tagsLabel}>
        {t['com.hive.integration.readwise.setting.tags-label']()}
      </h6>
      <TagsInlineEditor
        placeholder={
          <span className={styles.tagsPlaceholder}>
            {t['com.hive.integration.readwise.setting.tags-placeholder']()}
          </span>
        }
        className={styles.tagsEditor}
        tagMode="inline-tag"
        tags={adaptedTags}
        selectedTags={tagIds ?? []}
        onCreateTag={onCreateTag}
        onSelectTag={onSelectTag}
        onDeselectTag={onDeselectTag}
        tagColors={adaptedTagColors}
        onTagChange={onTagChange}
        onDeleteTag={onDeleteTag}
        modalMenu={true}
        menuClassName={styles.tagsMenu}
      />
    </li>
  );
};
